/**
 * How dates and times read everywhere in CE Hub: "Sept 25, 2026" and
 * "2:51 pm". One module for the Worker (labels it computes) and both SPAs.
 *
 * Inputs are what the database stores: ISO dates ("2026-09-25") and SQLite
 * `datetime('now')` strings ("2026-09-25 14:51:26", UTC). A date is a
 * calendar day and is never shifted by a time zone; a date-time is an
 * instant and is shown in the viewer's zone (the browser's, or the one given).
 * Anything that does not parse is returned as it came, so a legacy label
 * still reads rather than breaking.
 */

/** AP-style month abbreviations — the house style asks for "Sept". */
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];

const DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

/** "Sept 25, 2026" from an ISO date or anything that starts with one. */
export function formatDate(value: string): string {
  const m = DATE.exec(value);
  if (!m) return value;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}, ${m[1]}` : value;
}

/** "2:51 pm" from an hour and minute. */
export function formatClock(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "am" : "pm"}`;
}

/** The instant a stored UTC date-time string denotes, or null. */
export function parseDateTime(value: string): Date | null {
  const m = DATE_TIME.exec(value);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0)));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "Sept 25, 2026, 2:51 pm" for a stored UTC date-time, in `timeZone` (the
 * viewer's own when omitted). A bare date reads as a date; anything else as it came.
 */
export function formatDateTime(value: string, timeZone?: string): string {
  const d = parseDateTime(value);
  if (!d) return formatDate(value);
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", hourCycle: "h23" }).formatToParts(d);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const [y, mo, day, h, min] = [get("year"), get("month"), get("day"), get("hour"), get("minute")];
  if ([y, mo, day, h, min].some(Number.isNaN)) return value;
  return `${MONTHS[mo - 1]} ${day}, ${y}, ${formatClock(h, min)}`;
}
